import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/badge';
import { BADGE_VARIANT_BLUE_SOFT } from '@components/badge/src/badge.type';
import { SwitchToggle } from '@components/switch-toggle';
import classNames from 'classnames';

import {
  TOGGLE_SECTION_BADGE_NEW,
  TOGGLE_SECTION_SIZE_L,
  TOGGLE_SECTION_SIZE_S,
  ToggleSectionProps,
} from './toggle-section.type';

export const TOGGLE_SECTION_TEST_ID = 'toggle-section';
export const TOGGLE_SECTION_HEADER_TEST_ID = `${TOGGLE_SECTION_TEST_ID}-header`;
export const TOGGLE_SECTION_LABEL_TEST_ID = `${TOGGLE_SECTION_TEST_ID}-label`;
export const TOGGLE_SECTION_SUBLABEL_TEST_ID = `${TOGGLE_SECTION_TEST_ID}-sublabel`;
export const TOGGLE_SECTION_BADGE_TEST_ID = `${TOGGLE_SECTION_TEST_ID}-badge`;
export const TOGGLE_SECTION_CHILDREN_TEST_ID = `${TOGGLE_SECTION_TEST_ID}-children`;

const Header = ({
  label,
  subLabel,
  badge,
  loading = false,
  testIdPrefix,
}: {
  label: string;
  subLabel?: string;
  badge?: NonNullable<ToggleSectionProps['badge']>;
  loading?: boolean;
  testIdPrefix: string;
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <header data-testid={`${testIdPrefix}-header`} className='flex flex-col gap-1.5 w-full min-w-0' aria-busy='true'>
        <div
          data-testid={`${testIdPrefix}-label-skeleton`}
          className='h-4 w-40 max-w-full animate-pulse rounded bg-secondary'
        />
        {subLabel && (
          <div
            data-testid={`${testIdPrefix}-sublabel-skeleton`}
            className='h-3 w-56 max-w-full animate-pulse rounded bg-secondary'
          />
        )}
      </header>
    );
  }

  return (
    <header data-testid={`${testIdPrefix}-header`} className='flex flex-col gap-0.5 w-full min-w-0'>
      <div className='flex items-center gap-2'>
        <span data-testid={`${testIdPrefix}-label`} className='text-subtitle-m text-primary'>
          {label}
        </span>
        {badge === TOGGLE_SECTION_BADGE_NEW && (
          <Badge
            variant={BADGE_VARIANT_BLUE_SOFT}
            squared
            className='rounded px-1.5! py-0.5!'
            dataTestId={`${testIdPrefix}-badge`}>
            {t('common:new')}
          </Badge>
        )}
      </div>
      {subLabel && (
        <span data-testid={`${testIdPrefix}-sublabel`} className='text-body-xs text-secondary'>
          {subLabel}
        </span>
      )}
    </header>
  );
};

const ChildrenBlock = ({
  children,
  size,
  testIdPrefix,
}: {
  children: ReactNode;
  size: ToggleSectionProps['size'];
  testIdPrefix: string;
}) => (
  <div
    data-testid={`${testIdPrefix}-children`}
    className={classNames('border-l border-soft w-full', {
      'px-4': size === TOGGLE_SECTION_SIZE_L,
      'pl-4': size === TOGGLE_SECTION_SIZE_S,
    })}>
    {children}
  </div>
);

export const ToggleSection = ({
  value,
  onChange,
  label,
  subLabel,
  badge,
  disabled = false,
  loading = false,
  size = TOGGLE_SECTION_SIZE_L,
  children,
  className,
  dataTestId = TOGGLE_SECTION_TEST_ID,
}: ToggleSectionProps) => {
  const handleToggle = (next: boolean) => {
    if (disabled || loading) return;
    onChange(next);
  };

  const isOpen = Boolean(value && children && !loading);

  if (size === TOGGLE_SECTION_SIZE_S) {
    return (
      <section
        data-testid={dataTestId}
        className={classNames('flex flex-col gap-3 items-start py-4 w-full', className)}>
        <div className='flex gap-3 items-start w-full'>
          <SwitchToggle active={value} loading={loading} disabled={disabled} onToggleSwitch={handleToggle} />
          <Header label={label} subLabel={subLabel} badge={badge} loading={loading} testIdPrefix={dataTestId} />
        </div>
        {isOpen && (
          <ChildrenBlock size={size} testIdPrefix={dataTestId}>
            {children}
          </ChildrenBlock>
        )}
      </section>
    );
  }

  return (
    <section data-testid={dataTestId} className={classNames('flex gap-3 items-start py-4 w-full', className)}>
      <SwitchToggle active={value} loading={loading} disabled={disabled} onToggleSwitch={handleToggle} />
      <div className='flex flex-col gap-4 flex-1 min-w-0'>
        <Header label={label} subLabel={subLabel} badge={badge} loading={loading} testIdPrefix={dataTestId} />
        {isOpen && (
          <ChildrenBlock size={size} testIdPrefix={dataTestId}>
            {children}
          </ChildrenBlock>
        )}
      </div>
    </section>
  );
};
